/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from 'react-hot-toast';
import {usePostRequest} from "../../../../../../api/helper";
import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks";
import {SelectSensorStep, SELECT_SENSOR_STEP_NAME} from "./SelectSensorStep/SelectSensorStep";
import {
  selectConfigSensorWizardDefinePropertiesStep,
  selectConfigSensorWizardSelectSensorStep, selectConfigSensorWizardState,
  selectSensorBomData, setConfigSensorWizardDefinePropertiesStep, setConfigSensorWizardState, setSensorBomData,
  TDefinePropertiesStep,
} from "../../../../../../redux/ducks/AdasPrograms/slice";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import {MultiPageWizard} from "../../../../../components/MultiPageWizard/MultiPageWizard";
import {EAdasProgramsEditSensorDialogType} from "../types";
import {EAdasProgramSensorBomCheckboxTypes} from "../../types";
import {DEFINE_PROPERTIES_STEP_NAME, DefinePropertiesStep} from './DefinePropertiesStep/DefinePropertiesStep';
import {cloneDeep} from 'lodash';
import React from 'react';
import {PROPERTY_FIELDS, PROPERTY_KEY} from './DefinePropertiesStep/constants';

/**
 * Allows the user to add or edit a sensor in the sensor Bill of Materials for an
 * ADAS program.
 */
export const ConfigSensorWizard = () => {
  const steps = [SELECT_SENSOR_STEP_NAME, DEFINE_PROPERTIES_STEP_NAME]

  const dispatch = useAppDispatch()
  const adasProgramId = useAppSelector(state => selectSensorBomData(state)?.adasProgramId)
  const wizardState = useAppSelector(selectConfigSensorWizardState)
  const selectSensorStepState = useAppSelector(selectConfigSensorWizardSelectSensorStep)
  const definePropertiesStepState = useAppSelector(selectConfigSensorWizardDefinePropertiesStep)
  const action = wizardState?.action
  const isEditFromTable = action === EAdasProgramsEditSensorDialogType.EDIT_FROM_TABLE
  const isEditFromLocation = action === EAdasProgramsEditSensorDialogType.EDIT_FROM_LOCATION
  const isEdit = isEditFromTable || isEditFromLocation

  const [isValidating, setIsValidating] = React.useState<boolean>(false)

  /**
   * Handle for saving sensor.
   */
  const addSensorHandle = usePostRequest()

  /** Validate form */
  const validateProperties = () => {
    setIsValidating(true)
    let isValid = true
    const newStepState = cloneDeep(definePropertiesStepState)
    if (!newStepState) return

    for (const key in newStepState) {
      const property = key as keyof TDefinePropertiesStep
      /** Check for blank fields */
      if (newStepState[property].value === "" || newStepState[property].value === null || newStepState[property].value === undefined) {
        isValid = false
        newStepState[property].error = 'Property is required'
      } 
      /** Check if angle values are within allowed range */
      else if (PROPERTY_FIELDS.find(field => PROPERTY_KEY[field.id] === property)?.unit === "deg") {
        const angleValue = parseFloat(newStepState[property].value)
        const isSensorDesiredFov = property === 'sensorDesiredFovDegrees'
        const minVal = isSensorDesiredFov ? -179 : -180
        const maxVal = isSensorDesiredFov ? 179 : 180
        if (isNaN(angleValue) || angleValue < minVal || angleValue > maxVal) {
          isValid = false
          newStepState[property].error = `Value must be between ${minVal} and ${maxVal}`
        } else {
          newStepState[property].error = null
        }
      }
      /** Check if thickness values are within allowed range */
      else if (property === 'materialThicknessNominal' || property === 'materialThicknessMinimum' || property === 'materialThicknessMaximum') {
        const value = parseFloat(newStepState[property].value)
        if (isNaN(value) || value < 0) {
          isValid = false
          newStepState[property].error = 'Value must be greater than or equal to 0'
        } else {
          newStepState[property].error = null
        }
      }
      else {
        newStepState[property].error = null
      }
    }

    /**
     * Validates that the minimum, nominal, and maximum properties are within allowed ranges.
     *
     * @param {string} baseName - The base name of the property to validate. This will be used to construct the full property names for minimum, nominal, and maximum values.
     */
    const validateMinNominalMax = (baseName: string) => {
      const minProp = `${baseName}Minimum` as keyof TDefinePropertiesStep
      const nominalProp = `${baseName}Nominal` as keyof TDefinePropertiesStep
      const maxProp = `${baseName}Maximum` as keyof TDefinePropertiesStep
    
      const minValue = parseFloat(newStepState[minProp]?.value || '')
      const nominalValue = parseFloat(newStepState[nominalProp]?.value || '')
      const maxValue = parseFloat(newStepState[maxProp]?.value || '')
    
      if (!isNaN(minValue) && !isNaN(nominalValue) && !isNaN(maxValue)) {
        if (minValue > nominalValue && nominalValue > maxValue) {
          isValid = false
          newStepState[minProp].error = 'Minimum must be less than or equal to Nominal'
          newStepState[nominalProp].error = 'Nominal must be greater than or equal to Minimum and less than or equal to Maximum'
          newStepState[maxProp].error = 'Maximum must be greater than or equal to Nominal'
        } else if (minValue > nominalValue) {
          isValid = false
          newStepState[minProp].error = 'Minimum must be less than or equal to Nominal'
          newStepState[nominalProp].error = 'Nominal must be greater than or equal to Minimum'
        } else if (nominalValue > maxValue) {
          isValid = false
          newStepState[nominalProp].error = 'Nominal must be less than or equal to Maximum'
          newStepState[maxProp].error = 'Maximum must be greater than or equal to Nominal'
        }
      }
    }
    const baseNames = ['x', 'y', 'z', 'roll', 'pitch', 'yaw']  /** Properties that have min, nominal, and max values */
    baseNames.forEach(baseName => validateMinNominalMax(baseName))

    dispatch(setConfigSensorWizardDefinePropertiesStep(newStepState))
    setIsValidating(false)
    return isValid
  }
    
  /**
   * This handle is run when Finish is pressed. It will make a post request to the backend to save the sensor.
   * TODO: Modify to allow multi select for locationKey. This will require changing the backend to accept a list.
   */
  const handleSave = () => {
    if (validateProperties()) {
      let payload = {
        adasProgramId, ...wizardState,
      }
      let requestType = ''
      switch (action) {
        case EAdasProgramsEditSensorDialogType.ADD_FROM_LOCATION:
          requestType = 'addSensor'
          break
        case EAdasProgramsEditSensorDialogType.ADD_GLOBAL:
          requestType = 'addSensor'
          break
        case EAdasProgramsEditSensorDialogType.EDIT_FROM_LOCATION:
          requestType = 'editSensorAtLocation'
          break
        case EAdasProgramsEditSensorDialogType.EDIT_FROM_TABLE:
          requestType = 'editSensorAtLocation'
      }
      const url = `/HttpTriggerAdasPrograms?page=sensorBom&requestType=${requestType}`
      addSensorHandle(payload, null, 'POST', '', url).then((response: any) => {
        dispatch(setSensorBomData(response))
        /** Clear wizard state on finish */
        dispatch(setConfigSensorWizardState(null))
      }).catch((error: any) => {
        console.log(error)
        toast.error('Error saving sensor')
      })
    }
  }

  const selectSensorIsIncomplete = selectSensorStepState?.locationKey?.length === 0 
    || selectSensorStepState?.modality === ""
    || (!selectSensorStepState?.notSourced
      && (selectSensorStepState?.name === ""
      || selectSensorStepState?.id === "" || selectSensorStepState?.version === ""
      || (selectSensorStepState?.modality === EAdasProgramSensorBomCheckboxTypes.CAMERAS &&
        (selectSensorStepState?.processedResolution === null || selectSensorStepState?.processedResolution === undefined))))
  const definePropertiesIsIncomplete = definePropertiesStepState === null
  const isFinishDisabled = selectSensorIsIncomplete || definePropertiesIsIncomplete

  return (
    <ArgosFlexbox vertical style={{height: '100%', overflow: 'auto', flex: 1, justifyContent: "center", alignItems: "center"}}>
      <MultiPageWizard 
        steps={steps} 
        onFinish={() => handleSave()} 
        onClickClose={() => dispatch(setConfigSensorWizardState(null))}
        isNextDisabled={[selectSensorIsIncomplete, definePropertiesIsIncomplete]}  
        isFinishDisabled={isFinishDisabled}
        isFinishLoading={isValidating}
      >
        <SelectSensorStep />
        <DefinePropertiesStep />
      </MultiPageWizard>
    </ArgosFlexbox>
  )
}